import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIInterpretationSection = ({ 
  interpretation, 
  isLoading, 
  error, 
  onGenerate, 
  isVisible, 
  onToggle 
}) => {
  return (
    <section id="ai-interpretation" className="scroll-mt-20">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-primary/10 to-secondary/10 rounded-2xl blur-2xl" />
        <div className="relative bg-card/80 backdrop-blur-xl border-2 border-border/50 rounded-2xl shadow-cosmic overflow-hidden">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between px-5 md:px-7 py-5 hover:bg-muted/30 transition-all duration-300 group"
            aria-expanded={isVisible}
          >
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-accent via-primary to-secondary rounded-xl flex items-center justify-center mr-4 shadow-glow group-hover:shadow-glow-lg transition-all duration-300">
                <Icon name="Sparkles" size={24} color="#ffffff" />
              </div>
              <div className="text-left">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground font-thai">
                  การวิเคราะห์แผนภูมิด้วย AI
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground mt-0.5">AI-Powered Chart Interpretation</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {interpretation && (
                <span className="hidden sm:inline text-xs text-accent font-medium">
                  ✨ พร้อมแล้ว
                </span>
              )}
              <Icon 
                name={isVisible ? "ChevronUp" : "ChevronDown"} 
                size={24} 
                className="text-muted-foreground group-hover:text-primary transition-colors duration-300"
              />
            </div>
          </button>

          {isVisible && (
            <div className="border-t-2 border-border/30">
              <div className="p-5 md:p-7">
                {!interpretation && !isLoading && !error && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-glow">
                      <Icon name="Sparkles" size={32} color="#ffffff" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2 font-thai">
                      สร้างการวิเคราะห์แผนภูมิด้วย AI
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                      ใช้ปัญญาประดิษฐ์วิเคราะห์ตำแหน่งดาว มุมระหว่างดาว และบ้านโหราศาสตร์ เพื่อสร้างรายงานเชิงลึกในภาษาไทย
                    </p>
                    <Button
                      onClick={onGenerate}
                      variant="default"
                      size="lg"
                      iconName="Sparkles"
                      iconPosition="left"
                      className="bg-gradient-to-r from-accent via-primary to-secondary hover:shadow-glow-lg"
                    >
                      สร้างการวิเคราะห์
                    </Button>
                  </div>
                )}

                {isLoading && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse shadow-glow">
                      <Icon name="Loader" size={32} color="#ffffff" className="animate-spin" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground mb-2 font-thai">
                      กำลังวิเคราะห์แผนภูมิ...
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      AI กำลังวิเคราะห์ข้อมูลดาวเกิดของคุณ กรุณารอสักครู่
                    </p>
                  </div>
                )}

                {error && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-destructive mb-2 font-thai">
                      เกิดข้อผิดพลาด
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                      {error}
                    </p>
                    <Button
                      onClick={onGenerate}
                      variant="outline"
                      size="md"
                      iconName="RefreshCw"
                      iconPosition="left"
                    >
                      ลองอีกครั้ง
                    </Button>
                  </div>
                )}

                {interpretation && !isLoading && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                        <span className="text-xs text-muted-foreground">สร้างโดย AI</span>
                      </div>
                      <Button
                        onClick={onGenerate}
                        variant="ghost"
                        size="sm"
                        iconName="RefreshCw"
                        iconPosition="left"
                      >
                        สร้างใหม่
                      </Button>
                    </div>

                    <div className="prose prose-sm md:prose-base max-w-none">
                      <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-xl p-5 md:p-6 border border-border/30">
                        <div className="text-foreground whitespace-pre-wrap leading-relaxed font-thai">
                          {interpretation}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-accent/5 border border-accent/20 rounded-lg">
                      <Icon name="Info" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-muted-foreground">
                        <p className="font-thai">
                          <strong>หมายเหตุ:</strong> การวิเคราะห์นี้สร้างโดยปัญญาประดิษฐ์เพื่อเป็นแนวทางเบื้องต้น 
                          ควรใช้ร่วมกับการปรึกษานักโหราศาสตร์มืออาชีพสำหรับคำแนะนำที่ละเอียดยิ่งขึ้น
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AIInterpretationSection;